import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { AsyncCombobox } from '@/shared/components/custom/async-combobox/AsyncCombobox';
import { Toolbar } from '@/shared/components/custom/toolbar';
import { FiltersPopover } from '@/shared/components/custom/filters-popover';
import { FloatingLabelInput } from '@/shared/components/custom/form-field/FloatingLabelInput';
import { usePagination } from '@/shared/hooks/usePagination';
import { DEFAULT_PAGE_SIZE } from '@/shared/types/pagination';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useProjects } from '@/shared/hooks/useProjects';
import { useProjectTimeEntries } from '@/features/time-tracking/public';
import { useOpenTask } from '@/features/tasks/public';
import { TimeEntriesTable } from './TimeEntriesTable';
import { exportTimeEntriesCsv } from '../lib/exportTimeEntriesCsv';

export function ProjectTimeReport() {
  const { t } = useTranslation('reports');
  const { data: projects = [] } = useProjects();
  const [projectId, setProjectId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const activeFiltersCount = search ? 1 : 0;

  const projectOptions = projects.map((p) => ({ id: p.id, label: p.name ?? '—' }));

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const listQuery = useProjectTimeEntries(projectId, {
    page: pageIndex + 1,
    pageSize,
    searchTerm: debouncedSearch,
  });
  const pagination = usePagination({
    totalItems: listQuery.data?.totalCount ?? 0,
    pageIndex,
    onPageChange: setPageIndex,
    pageSize,
    onPageSizeChange: setPageSize,
  });

  const entries = listQuery.data?.items ?? [];
  const openTask = useOpenTask();

  function handleExport() {
    exportTimeEntriesCsv(entries, {
      filename: 'time-report-project.csv',
      headers: [
        t('table.columns.task'),
        t('table.columns.project'),
        t('table.columns.person'),
        t('table.columns.start'),
        t('table.columns.end'),
        t('table.columns.duration'),
        t('table.columns.billable'),
      ],
      billableLabels: { yes: t('table.billableYes'), no: t('table.billableNo') },
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <Toolbar className="py-0">
        <Toolbar.Actions>
          <AsyncCombobox
            queryKey={['reports-project-options']}
            staticOptions={projectOptions}
            value={projectId}
            onChange={setProjectId}
            clearable
            placeholder={t('filters.selectProject')}
            searchPlaceholder={t('filters.search')}
            triggerClassName="w-56"
            data-testid="reports-project-select"
          />

          <FiltersPopover activeCount={activeFiltersCount} label={t('filters.trigger')}>
            <FiltersPopover.Content>
              <FloatingLabelInput
                id="reports-project-search"
                label={t('filters.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                hasValue={!!search}
                data-testid="reports-search"
              />
              {activeFiltersCount > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setSearch('')} className="w-full">
                  {t('filters.reset')}
                </Button>
              )}
            </FiltersPopover.Content>
          </FiltersPopover>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                onClick={handleExport}
                disabled={entries.length === 0}
                data-testid="reports-export-btn"
              >
                <Download className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('actions.export')}</TooltipContent>
          </Tooltip>
        </Toolbar.Actions>
      </Toolbar>

      {!projectId ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t('table.selectPrompt')}</p>
      ) : (
        <TimeEntriesTable
          entries={entries}
          isLoading={listQuery.isLoading}
          isError={listQuery.isError}
          pagination={pagination}
          onTaskClick={openTask}
        />
      )}
    </div>
  );
}
